"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, CreditCard, ExternalLink, LogOut } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { ProductSwitcher } from "@/components/product-switcher";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { CallHistoryList } from "./call-history-list";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { CallHistoryItem } from "./call-history-list";
import type { Organization } from "@/lib/types";
interface RealtimeSidebarProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  historyItems: CallHistoryItem[];
  isHistoryLoading?: boolean;
  currentItemId?: string | null;
  onNewCall: () => void;
  onItemClick: (itemId: string) => void;
  onItemDeleted?: (itemId: string) => void;
  className?: string;
}
export function RealtimeSidebar(t0) {
  const $ = _c(86);
  const {
    organizations,
    selectedOrganization,
    onSelectOrganization,
    historyItems,
    isHistoryLoading: t1,
    currentItemId,
    onNewCall,
    onItemClick,
    onItemDeleted,
    className
  } = t0;
  const isHistoryLoading = t1 === undefined ? false : t1;
  let switcherOrganizations;
  let t2;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp);
    t2 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t2;
  } else {
    switcherOrganizations = $[2];
    t2 = $[3];
  }
  const switcherSelectedOrganization = t2;
  const router = useRouter();
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t3;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t3 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://lounge.llmgateway.io/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const logout = t3;
  if (isUserLoading) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><div className="flex w-full items-center gap-2"><ProductSwitcher /><Badge>Voice</Badge></div></div><StudioNav /></SidebarHeader>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== className) {
      t5 = <Sidebar className={className}>{t4}</Sidebar>;
      $[9] = className;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (!user) {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex w-full items-center gap-2"><ProductSwitcher /><Badge>Voice</Badge></div>;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="font-medium mb-2">Sign in required</div>;
      t6 = <p className="text-muted-foreground mb-3">Please sign in to start voice calls.</p>;
      $[12] = t5;
      $[13] = t6;
    } else {
      t5 = $[12];
      t6 = $[13];
    }
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t4}<div className="w-full rounded-md border p-4 text-sm">{t5}{t6}<div className="flex items-center justify-end gap-2">{t7}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== className) {
      t9 = <Sidebar className={className}>{t8}</Sidebar>;
      $[16] = className;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  const t4 = (className ?? "") + " max-md:hidden";
  let t5;
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SidebarMenuItem><ProductSwitcher /></SidebarMenuItem>;
    t6 = <SidebarChatSearch disabled={true} />;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  let t7;
  if ($[20] !== onNewCall) {
    t7 = <SidebarMenu>{t5}{t6}<SidebarNewAction label="New Call" onAction={onNewCall} /></SidebarMenu>;
    $[20] = onNewCall;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <StudioNav />;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7) {
    t9 = <SidebarHeader>{t7}{t8}</SidebarHeader>;
    $[23] = t7;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== onSelectOrganization || $[27] !== switcherOrganizations || $[28] !== switcherSelectedOrganization) {
    t11 = switcherOrganizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[26] = onSelectOrganization;
    $[27] = switcherOrganizations;
    $[28] = switcherSelectedOrganization;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <div>{t10}{t11}</div>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = sidebarState === "collapsed" && !isMobile;
  let t14;
  if ($[32] !== currentItemId || $[33] !== historyItems || $[34] !== isHistoryLoading || $[35] !== onItemClick || $[36] !== onItemDeleted) {
    t14 = <CallHistoryList items={historyItems} isLoading={isHistoryLoading} currentItemId={currentItemId} onItemClick={onItemClick} onItemDeleted={onItemDeleted} />;
    $[32] = currentItemId;
    $[33] = historyItems;
    $[34] = isHistoryLoading;
    $[35] = onItemClick;
    $[36] = onItemDeleted;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13 || $[39] !== t14) {
    t15 = <div aria-hidden={t13} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t14}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t12 || $[42] !== t15) {
    t16 = <SidebarContent className="overflow-hidden pb-2">{t12}{t15}</SidebarContent>;
    $[41] = t12;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SidebarLoungePoints />;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const t18 = switcherSelectedOrganization ?? organization;
  const t19 = !switcherSelectedOrganization;
  let t20;
  if ($[45] !== isOrgLoading || $[46] !== t18 || $[47] !== t19) {
    t20 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t18} isLoading={isOrgLoading} isChatPlanOrg={t19} /></div>;
    $[45] = isOrgLoading;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const t21 = user?.name ?? "User";
  let t22;
  if ($[49] !== user?.name) {
    t22 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2) ?? "U";
    $[49] = user?.name;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t22) {
    t23 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t22}</span></div>;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  const t24 = user?.name;
  let t25;
  if ($[53] !== t24) {
    t25 = <span className="truncate font-semibold">{t24}</span>;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  const t26 = user?.email;
  let t27;
  if ($[55] !== t26) {
    t27 = <span className="truncate text-xs text-muted-foreground">{t26}</span>;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t25 || $[58] !== t27) {
    t28 = <div className="grid flex-1 text-left text-sm leading-tight">{t25}{t27}</div>;
    $[57] = t25;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t21 || $[62] !== t23 || $[63] !== t28) {
    t30 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t21} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t23}{t28}{t29}</SidebarMenuButton></DropdownMenuTrigger>;
    $[61] = t21;
    $[62] = t23;
    $[63] = t28;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  let t32;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t32 = <DropdownMenuSeparator />;
    $[65] = t31;
    $[66] = t32;
  } else {
    t31 = $[65];
    t32 = $[66];
  }
  let t33;
  let t34;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t34 = <DropdownMenuSeparator />;
    $[67] = t33;
    $[68] = t34;
  } else {
    t33 = $[67];
    t34 = $[68];
  }
  let t35;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span>Theme</span>;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  let t37;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp3}>{t35}<div onClick={_temp4} onKeyDown={_temp5}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t37 = <DropdownMenuSeparator />;
    $[70] = t36;
    $[71] = t37;
  } else {
    t36 = $[70];
    t37 = $[71];
  }
  let t38;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <LogOut className="mr-2 h-4 w-4" />;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] !== logout) {
    t39 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t31}{t32}{t33}{t34}{t36}{t37}<DropdownMenuItem onClick={logout}>{t38}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[73] = logout;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== t30 || $[76] !== t39) {
    t40 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t30}{t39}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[75] = t30;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== t20 || $[79] !== t40) {
    t41 = <SidebarFooter>{t17}{t20}{t40}</SidebarFooter>;
    $[78] = t20;
    $[79] = t40;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== t16 || $[82] !== t4 || $[83] !== t41 || $[84] !== t9) {
    t42 = <Sidebar collapsible="icon" className={t4}>{t9}{t16}{t41}</Sidebar>;
    $[81] = t16;
    $[82] = t4;
    $[83] = t41;
    $[84] = t9;
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  return t42;
}
function _temp5(event_1) {
  return event_1.stopPropagation();
}
function _temp4(event_0) {
  return event_0.stopPropagation();
}
function _temp3(event) {
  return event.preventDefault();
}
function _temp2(n) {
  return n[0];
}
function _temp(org) {
  return org.kind === "default";
}